import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DropdownTextField from "../form-components/DropdownTextField";
import MapSpotSelector from "../common/MapSpotSelector";
import IconPickerModal from "./IconPickerModal";
import { updateBooth } from "../../api/boothApi";
import { submitApproval } from "../../api/approvalApi";
import { useToast } from "../../context/ToastContext";
import { hasRequiredName } from '../../utils/validators';
import { CONTENT_TYPES } from '../../constants/contentTypes';
import { IconMap } from '../../utils/IconMap';

export default function EditBoothModal({ open, onClose, boothId, initialData, actionType, contentName, usedMapSpotIds = new Set(), onSaved, approvalMode = false }) {
  const [localizedPairs, setLocalizedPairs] = useState({ key: '', values: [] });
  const [mapSpotId,      setMapSpotId]      = useState('');
  const [iconKey,        setIconKey]        = useState('');
  const [pickerOpen,     setPickerOpen]     = useState(false);
  const [saving,         setSaving]         = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (!open || !initialData) return;
    setLocalizedPairs(initialData.localizedPairs ?? { key: '', values: [] });
    setMapSpotId(initialData.mapSpotId ?? '');
    setIconKey(initialData.iconKey ?? '');
  }, [open, initialData]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateBooth(boothId, { localizedPairs, mapSpotId, iconKey: iconKey || null, modifiedBy: 'system' });

      // The PUT above already auto-creates the pending edit proposal server-side
      // (draft → direct update; approved/rejected-edit → auto-propose). The one
      // case it doesn't cover is resubmitting a rejected CREATE, which needs an
      // explicit fresh 'create' proposal once the draft row has been corrected.
      if (actionType === 'create') {
        const name = localizedPairs?.values?.find((v) => v.localeId?.startsWith('en'))?.value
          || localizedPairs?.values?.[0]?.value
          || contentName
          || `Booth ${boothId}`;
        await submitApproval({
          contentType: CONTENT_TYPES.BOOTH,
          contentId:   boothId,
          contentName: name,
          actionType:  'create',
        });
      }

      toast.success(actionType || approvalMode ? 'Edit submitted for approval!' : 'Booth updated successfully');
      onSaved?.(actionType || approvalMode ? 'pending' : 'draft');
    } catch {
      // error shown globally via axios interceptor → toast
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
    >
      <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', pr: 6, py: 2.5 }}>
        <Typography variant="h6" fontWeight={700} component="span">
          Edit Booth
        </Typography>
        <IconButton
          onClick={(e) => { e.currentTarget.blur(); onClose(); }}
          size="small"
          sx={{ position: 'absolute', right: 10, top: 12, color: 'white' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Box display="flex" flexDirection="column" gap={2}>
          <MapSpotSelector
            value={mapSpotId}
            onChange={(id) => setMapSpotId(id)}
            required
            disabledIds={usedMapSpotIds}
          />

          <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: 'text.secondary', mb: -1 }}>
            Booth Name <span style={{ color: '#d32f2f', marginLeft: 3 }}>*</span>
          </Typography>
          <DropdownTextField
            label="Booth Name"
            values={localizedPairs?.values}
            onChange={(newValues) => setLocalizedPairs((prev) => ({ ...prev, values: newValues }))}
            attribute="value"
            required
          />

          <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: 'text.secondary', mb: -1 }}>
            Icon
          </Typography>
          <Box
            onClick={() => setPickerOpen(true)}
            sx={{
              display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer',
              border: '1px solid', borderColor: 'grey.300', borderRadius: 2, p: 1.25,
              '&:hover': { borderColor: 'primary.main', bgcolor: 'rgba(10,50,109,0.03)' },
            }}
          >
            <Box sx={{
              bgcolor: '#0a326d', borderRadius: '10px', width: 40, height: 40,
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              {iconKey && (
                <Box component="img" src={IconMap[`bth_${iconKey}`]} alt="" sx={{ width: 24, height: 24 }} />
              )}
            </Box>
            <Typography variant="body2" sx={{ color: iconKey ? 'text.primary' : 'text.secondary' }}>
              {iconKey ? 'Change icon' : 'Choose an icon (optional)'}
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <IconPickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        value={iconKey}
        onSelect={setIconKey}
      />

      <DialogActions sx={{ justifyContent: 'center', p: 3, bgcolor: 'grey.50' }}>
        <Button onClick={onClose} sx={{ mr: 2 }}>Cancel</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving || !mapSpotId || !hasRequiredName(localizedPairs?.values)}
          size="large"
          sx={{ px: 6, py: 1.5, fontWeight: 700, borderRadius: 2 }}
        >
          {saving ? 'Saving…' : actionType ? 'Save & Resubmit' : approvalMode ? 'Save & Submit for Approval' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
